import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { isNil } from 'lodash-es';

import { Nullable } from '~types/Nullable';

interface SettlementLineRowProps {
  name: string;
  quantity: number;
  rate: Nullable<number>;
  totalAmount: number;
  wasRate: number | null;
  wasTotalAmount: number | null;
  onRemove: (() => void) | null;
  currencyFormatter: (amount: number) => string;
}

function WasAmount({ amount }: { amount: string }) {
  return (
    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
      {t('approvals.driver_pay.settlements.lines.was', { amount })}
    </Typography>
  );
}

export default function SettlementLineRow({
  name,
  quantity,
  rate,
  totalAmount,
  wasRate,
  wasTotalAmount,
  onRemove,
  currencyFormatter,
}: SettlementLineRowProps) {
  return (
    <Box
      data-test-id="page-approvals__driver-pay__settlement-lines__line"
      sx={(theme) => ({
        alignItems: 'center',
        borderBottom: `solid 1px ${theme.semantic.border}`,
        display: 'grid',
        gap: 2,
        gridTemplateColumns: 'minmax(200px, 3fr) 1fr 1fr 1.5fr 48px',
        // tall enough for the remove button without the row growing when it appears
        minHeight: '44px',
        px: 1,
      })}
    >
      <Typography variant="body2">{name}</Typography>
      <Typography variant="body2" sx={{ textAlign: 'right' }}>
        {quantity}
      </Typography>
      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="body2">
          {isNil(rate) ? '–' : currencyFormatter(rate)}
        </Typography>
        {wasRate !== null && <WasAmount amount={currencyFormatter(wasRate)} />}
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {currencyFormatter(totalAmount)}
        </Typography>
        {wasTotalAmount !== null && (
          <WasAmount amount={currencyFormatter(wasTotalAmount)} />
        )}
      </Box>
      <Box>
        {onRemove && (
          <Tooltip title={t('approvals.driver_pay.settlements.lines.remove')}>
            <IconButton
              aria-label={t('approvals.driver_pay.settlements.lines.remove')}
              onClick={onRemove}
              size="small"
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}
